import { z } from 'zod';

/**
 * Texto opcional: `undefined` = não alterar (PATCH); "" ou espaços = limpar (null).
 */
export const optionalText = (max: number, label = 'Campo') =>
  z
    .string({ invalid_type_error: `${label} inválido` })
    .trim()
    .max(max, `${label}: máximo de ${max} caracteres`)
    .nullish()
    .transform((v) => (v === undefined ? undefined : v ? v : null));

export const requiredText = (max: number, label: string, min = 1) =>
  z
    .string({ required_error: `${label} é obrigatório`, invalid_type_error: `${label} inválido` })
    .trim()
    .min(min, min <= 1 ? `${label} é obrigatório` : `${label}: mínimo de ${min} caracteres`)
    .max(max, `${label}: máximo de ${max} caracteres`);

export const idSchema = z.string({ required_error: 'ID obrigatório' }).uuid('ID inválido');
export const idParamSchema = z.object({ id: idSchema });

/** Valor em reais vindo de input numérico ou string ("12,50" também é aceito). */
export const moneySchema = z.preprocess(
  (v) => {
    if (typeof v !== 'string') return v;
    const s = v.trim().replace(/^R\$\s*/, '');
    if (s === '') return undefined;
    // "1.234,56" (pt-BR) -> 1234.56 ; "1234.56" -> 1234.56
    return Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s);
  },
  z
    .number({ invalid_type_error: 'Valor inválido', required_error: 'Valor obrigatório' })
    .finite('Valor inválido')
    .min(0, 'O valor não pode ser negativo')
    .max(10_000_000, 'Valor muito alto')
    .transform((n) => Math.round(n * 100) / 100),
);

export const intSchema = (label: string, min: number, max: number) =>
  z.preprocess(
    (v) => (typeof v === 'string' && v.trim() !== '' ? Number(v) : v),
    z
      .number({ invalid_type_error: `${label} inválido`, required_error: `${label} obrigatório` })
      .int(`${label} deve ser um número inteiro`)
      .min(min, min === 0 ? `${label} não pode ser negativo` : `${label}: mínimo ${min}`)
      .max(max, `${label}: máximo ${max}`),
  );

export const dateOnlySchema = z
  .string({ required_error: 'Data obrigatória' })
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida (use AAAA-MM-DD)')
  .refine((v) => !Number.isNaN(Date.parse(`${v}T12:00:00Z`)), 'Data inválida');

export const timeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Hora inválida (use HH:mm)');

export const booleanQuery = z.preprocess(
  (v) => (v === 'true' || v === '1' ? true : v === 'false' || v === '0' ? false : v),
  z.boolean().optional(),
);

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().trim().max(120).optional(),
});

export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export interface Paginated<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}
